import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Download, Loader2, ArrowLeft, ScanLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { makeQR, verifyQR, scaledQuietZone } from "@/lib/qr";
import { referencePageUrl, referenceQrUrl } from "@/lib/publicUrl";
import api from "@/lib/api";
import { BRAND } from "@/components/Brand";

const SIZES = { compact: 220, standard: 300, large: 380 };

const THEMES = {
  navy: { name: "Navy", border: "#0A2540", badge: "#0A2540", accent: "#0D9488", qr: "#0A2540" },
  teal: { name: "Teal", border: "#0F766E", badge: "#0F766E", accent: "#99F6E4", qr: "#0F766E" },
  slate: { name: "Slate", border: "#334155", badge: "#334155", accent: "#94A3B8", qr: "#1E293B" },
  crimson: { name: "Crimson", border: "#7F1D1D", badge: "#7F1D1D", accent: "#FCA5A5", qr: "#7F1D1D" },
  forest: { name: "Forest", border: "#14532D", badge: "#14532D", accent: "#86EFAC", qr: "#14532D" },
};

export default function PosterKit() {
  const { publicId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [cta, setCta] = useState("Scan to view the full references & sources");
  const [sizeKey, setSizeKey] = useState("standard");
  const [themeKey, setThemeKey] = useState("navy");
  const [downloading, setDownloading] = useState(false);
  const qrBox = useRef(null);
  const qrInst = useRef(null);

  const publicUrl = useMemo(() => referencePageUrl(publicId), [publicId]);
  const qrUrl = useMemo(() => referenceQrUrl(publicId), [publicId]);
  const qrPx = SIZES[sizeKey];
  const theme = THEMES[themeKey];

  useEffect(() => {
    api.get(`/public/reference/${publicId}`)
      .then(({ data }) => { setData(data); setStatus("ok"); document.title = `Poster Kit — ${data.title} | ${BRAND.name}`; })
      .catch(() => setStatus("notfound"));
  }, [publicId]);

  useEffect(() => {
    if (status !== "ok") return;
    qrInst.current = makeQR({ data: qrUrl, size: qrPx, fgColor: theme.qr, bgColor: "#FFFFFF", margin: 16, errorCorrectionLevel: "M" });
    if (qrBox.current) { qrBox.current.innerHTML = ""; qrInst.current.append(qrBox.current); }
  }, [status, qrUrl, qrPx, theme.qr]);

  const downloadPosterQR = async () => {
    setDownloading(true);
    try {
      const dlSize = 2048;
      const dlMargin = scaledQuietZone(16, qrPx, dlSize);
      const opts = { data: qrUrl, fgColor: theme.qr, bgColor: "#FFFFFF", margin: dlMargin, errorCorrectionLevel: "M", size: dlSize };
      const res = await verifyQR(qrUrl, opts);
      if (!res.ok) { toast.error(res.reason); return; }
      const inst = makeQR(opts);
      await inst.download({ name: `poster-qr-${publicId}`, extension: "png" });
      toast.success("High-resolution poster QR downloaded (2048px).");
    } catch (e) {
      toast.error("Download failed: " + (e?.message || "error"));
    } finally { setDownloading(false); }
  };

  if (status === "loading") return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0D9488]" /></div>;
  if (status === "notfound") return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 text-center">
      <h1 className="text-2xl font-bold text-slate-900">Page unavailable</h1>
      <p className="mt-2 text-sm text-slate-600">This reference page may have been unpublished or deleted.</p>
      <Button asChild className="mt-6 bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/">Go to {BRAND.name}</Link></Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Controls */}
      <div className="no-print border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-end justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" aria-label="Back"><Link to="/projects"><ArrowLeft className="h-5 w-5" /></Link></Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-900">Poster Kit</h1>
              <p className="text-xs text-slate-500">A print-ready caption block for your research poster.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="cta" className="text-xs">Call to action</Label>
              <Input id="cta" value={cta} onChange={(e) => setCta(e.target.value)} className="w-64" data-testid="poster-cta-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="size" className="text-xs">QR size</Label>
              <Select value={sizeKey} onValueChange={setSizeKey}>
                <SelectTrigger id="size" className="w-32" data-testid="poster-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compact">Compact</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="theme" className="text-xs">Theme</Label>
              <Select value={themeKey} onValueChange={setThemeKey}>
                <SelectTrigger id="theme" className="w-32" data-testid="poster-theme-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(THEMES).map(([k, t]) => (
                    <SelectItem key={k} value={k} data-testid={`poster-theme-${k}`}>
                      <span className="flex items-center gap-2">
                        <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: t.border }} />{t.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={downloadPosterQR} disabled={downloading} data-testid="poster-download-qr">
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} QR (2048px)
            </Button>
            <Button onClick={() => window.print()} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="poster-print-btn">
              <Printer className="mr-2 h-4 w-4" /> Print caption block
            </Button>
          </div>
        </div>
      </div>

      {/* Printable caption block */}
      <div className="flex justify-center px-4 py-10">
        <div className="poster-block w-full max-w-2xl rounded-2xl border-2 bg-white p-10 text-center shadow-sm" style={{ borderColor: theme.border }} data-testid="poster-block">
          <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-white" style={{ backgroundColor: theme.badge }}>
            <ScanLine className="h-4 w-4" style={{ color: theme.accent }} /> {cta}
          </div>
          <div className="mt-8 flex justify-center">
            <div ref={qrBox} className="rounded-xl border border-slate-200 p-4" data-testid="poster-qr" role="img" aria-label="QR code linking to the reference page" />
          </div>
          <h2 className="mt-8 text-3xl font-bold tracking-tight text-slate-900">{data.title}</h2>
          {(data.author_name || data.institution) && (
            <p className="mt-2 text-lg text-slate-600">
              {data.author_name}{data.author_name && data.institution ? " · " : ""}{data.institution}
            </p>
          )}
          <p className="mt-6 break-all font-mono text-sm text-slate-600">{publicUrl}</p>
          <p className="mt-8 text-xs font-medium text-slate-500">Created with {BRAND.name}</p>
        </div>
      </div>
    </div>
  );
}
